import { agpAxiosClient } from '../../Common/api/ABHub/ABHubTokenConfig';
import {
    isSreHealthAvailable,
    isValidCloudOrEnvironment as isValidSreCloudOrEnvironment,
} from '../../Common/util/sreClouds';
import { CancelToken } from 'axios';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../Common/api/ABHub/ABHubGlobalConfig';
import { RequestResult } from '../../AGCP/models/AirGapMessage';
import {
    SreEntityType,
    SreInflightItem,
    SreInflightSearchQuery,
    SreInflightSearchResponse,
    SreLegacyHealthResponse,
    SreProgressResponse,
    SreStageState,
    SreValidationFindingsResponse,
    SRE_STAGE_STATE_NAMES,
} from '../models/SreHealth';
import { runLocalInflightQuery } from './sreInflightLocalQuery';

export enum AgrmType {
    Deployment = 'Deployment',
    Build = 'Build',
}

// DEPLOYMENTS /BUILDS------------------------------------------------------
export const checkAgrmRestartPossible = async (
    uniqueIdentifier: string,
    type: AgrmType,
    serviceTreeId: string,
    cancelToken?: CancelToken
): Promise<RequestResult<boolean>> => {
    const agrmUrl = getConfig(GlobalConfigProperties.AgrmUrl);
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isValidCloudOrEnvironment(cloud, environment)) {
        return createInvalidEnvironmentResult<boolean>(cloud, environment);
    }

    const response = await agpAxiosClient.get<RequestResult<boolean>>(
        agrmUrl +
            `/api/${type.toLowerCase()}/restartcheck/${encodeURIComponent(
                serviceTreeId
            )}/${encodeURIComponent(uniqueIdentifier)}`,
        { cancelToken }
    );

    return response.data;
};

export const restartAgrmProcess = async (
    uniqueIdentifier: string,
    type: string,
    serviceTreeId: string,
    cancelToken?: CancelToken
): Promise<RequestResult<boolean>> => {
    const agrmUrl = getConfig(GlobalConfigProperties.AgrmUrl);
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isValidCloudOrEnvironment(cloud, environment)) {
        return createInvalidEnvironmentResult<boolean>(cloud, environment);
    }

    const response = await agpAxiosClient.post<RequestResult<boolean>>(
        agrmUrl +
            `/api/${type.toLowerCase()}/restart/${encodeURIComponent(
                serviceTreeId
            )}/${encodeURIComponent(uniqueIdentifier)}`,
        { cancelToken }
    );

    return response.data;
};

export const restoreBuildToShare = async (
    uniqueIdentifier: string,
    cancelToken?: CancelToken
): Promise<RequestResult<boolean>> => {
    const agrmUrl = getConfig(GlobalConfigProperties.AgrmUrl);
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isValidCloudOrEnvironment(cloud, environment)) {
        return createInvalidEnvironmentResult<boolean>(cloud, environment);
    }

    const response = await agpAxiosClient.post<RequestResult<boolean>>(
        agrmUrl + `/api/build/restore/${encodeURIComponent(uniqueIdentifier)}`,
        { cancelToken }
    );

    return response.data;
};

export const restoreBuildToShareCheck = async (
    uniqueIdentifier: string,
    cancelToken?: CancelToken
): Promise<RequestResult<boolean>> => {
    const agrmUrl = getConfig(GlobalConfigProperties.AgrmUrl);
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isValidCloudOrEnvironment(cloud, environment)) {
        return createInvalidEnvironmentResult<boolean>(cloud, environment);
    }

    const response = await agpAxiosClient.get<RequestResult<boolean>>(
        agrmUrl +
            `/api/build/restorecheck/${encodeURIComponent(uniqueIdentifier)}`,
        { cancelToken }
    );

    return response.data;
};

export const getAgrmData = async (
    uniqueIdentifier: string,
    agrmType: AgrmType,
    cancelToken?: CancelToken
): Promise<RequestResult<boolean>> => {
    const agrmUrl = getConfig(GlobalConfigProperties.AgrmUrl);
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isValidCloudOrEnvironment(cloud, environment)) {
        return createInvalidEnvironmentResult<boolean>(cloud, environment);
    }

    const convertedAgrmType =
        agrmType === AgrmType.Deployment ? agrmType : agrmType.toLowerCase();
    const response = await agpAxiosClient.get<RequestResult<boolean>>(
        agrmUrl +
            `/api/${convertedAgrmType}/${encodeURIComponent(uniqueIdentifier)}`,
        { cancelToken }
    );

    return response.data;
};

export const getAgrmOrchestrations = async (
    uniqueIdentifier: string,
    type: string,
    cancelToken?: CancelToken
): Promise<RequestResult<boolean>> => {
    const agrmUrl = getConfig(GlobalConfigProperties.AgrmUrl);
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);
    const convertedType =
        type == AgrmType.Deployment ? type : type.toLowerCase();

    if (!isValidCloudOrEnvironment(cloud, environment)) {
        return createInvalidEnvironmentResult<boolean>(cloud, environment);
    }

    const response = await agpAxiosClient.get<RequestResult<boolean>>(
        agrmUrl +
            `/api/${convertedType}/orchestration/${encodeURIComponent(
                uniqueIdentifier
            )}`,
        { cancelToken }
    );

    return response.data;
};

export const getAgrmStates = async (
    uniqueIdentifier: string,
    type: string,
    cancelToken?: CancelToken
): Promise<RequestResult<boolean>> => {
    const agrmUrl = getConfig(GlobalConfigProperties.AgrmUrl);
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);
    const convertedType =
        type === AgrmType.Deployment ? type : type.toLowerCase();

    if (!isValidCloudOrEnvironment(cloud, environment)) {
        return createInvalidEnvironmentResult<boolean>(cloud, environment);
    }

    const response = await agpAxiosClient.get<RequestResult<boolean>>(
        agrmUrl +
            `/api/${convertedType}/states/${encodeURIComponent(
                uniqueIdentifier
            )}`,
        { cancelToken }
    );

    return response.data;
};

export const checkSreDeploymentControl = async (
    uniqueIdentifier: string,
    type: AgrmType,
    cancelToken?: CancelToken
): Promise<RequestResult<boolean>> => {
    const agrmUrl = getConfig(GlobalConfigProperties.AgrmUrl);
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isValidCloudOrEnvironment(cloud, environment)) {
        return createInvalidEnvironmentResult<boolean>(cloud, environment);
    }

    const response = await agpAxiosClient.get<RequestResult<boolean>>(
        agrmUrl +
            `/api/${type.toLowerCase()}/control/${encodeURIComponent(
                uniqueIdentifier
            )}/check`,
        { cancelToken }
    );

    return response.data;
};

export const createSreDeploymentControl = async (
    uniqueIdentifier: string,
    type: AgrmType,
    cancelToken?: CancelToken
): Promise<RequestResult<string>> => {
    const agrmUrl = getConfig(GlobalConfigProperties.AgrmUrl);
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isValidCloudOrEnvironment(cloud, environment)) {
        return createInvalidEnvironmentResult<string>(cloud, environment);
    }

    const response = await agpAxiosClient.post<RequestResult<string>>(
        agrmUrl +
            `/api/${type.toLowerCase()}/control/${encodeURIComponent(
                uniqueIdentifier
            )}/create`,
        { cancelToken }
    );

    return response.data;
};

export const deleteSreDeploymentControl = async (
    uniqueIdentifier: string,
    type: AgrmType,
    cancelToken?: CancelToken
): Promise<RequestResult<number>> => {
    const agrmUrl = getConfig(GlobalConfigProperties.AgrmUrl);
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isValidCloudOrEnvironment(cloud, environment)) {
        return createInvalidEnvironmentResult<number>(cloud, environment);
    }

    const response = await agpAxiosClient.delete<RequestResult<number>>(
        agrmUrl +
            `/api/${type.toLowerCase()}/control/${encodeURIComponent(
                uniqueIdentifier
            )}/delete`,
        { cancelToken }
    );

    return response.data;
};

export const checkSreHealth = async (
    type: AgrmType,
    cancelToken?: CancelToken
): Promise<RequestResult<SreLegacyHealthResponse> | null> => {
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isSreHealthAvailable(cloud, environment)) {
        return null;
    }

    const agrmUrl = getSreHealthUrl();
    const response = await agpAxiosClient.get<
        RequestResult<SreLegacyHealthResponse>
    >(agrmUrl + `/api/${type.toLowerCase()}/health`, { cancelToken });

    return response.data;
};

export const getInflightProgress = async (
    uniqueIdentifier: string,
    type: AgrmType,
    cancelToken?: CancelToken
): Promise<RequestResult<SreProgressResponse>> => {
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isSreHealthAvailable(cloud, environment)) {
        return createInvalidEnvironmentResult<SreProgressResponse>(
            cloud,
            environment
        );
    }

    const agrmUrl = getSreHealthUrl();
    const response = await agpAxiosClient.get<
        RequestResult<SreProgressResponse>
    >(
        agrmUrl +
            `/api/${type.toLowerCase()}/health/details/${encodeURIComponent(
                uniqueIdentifier
            )}`,
        { cancelToken }
    );

    return response.data;
};

/**
 * Serializes the query into the repeatable query-string form the search endpoint expects.
 */
const toSearchParams = (query: SreInflightSearchQuery): URLSearchParams => {
    const params = new URLSearchParams();

    const append = (name: string, value: unknown) => {
        if (value === undefined || value === null || value === '') return;
        params.append(name, String(value));
    };

    append('uniqueIdentifier', query.uniqueIdentifier);
    append('search', query.search);
    append('orchestrationType', query.orchestrationType);
    append('pipeline', query.pipeline);
    append('region', query.region);
    append('agedOlderThanHours', query.agedOlderThanHours);
    append('minRetryCount', query.minRetryCount);
    append('sortBy', query.sortBy);
    append('sortDirection', query.sortDirection);
    append('page', query.page);
    append('pageSize', query.pageSize);

    // Repeated rather than comma-joined, which is what ASP.NET model binding expects for int[].
    query.stage?.forEach((s) => params.append('stage', String(s)));
    query.stageState?.forEach((s) => params.append('stageState', String(s)));

    return params;
};

/**
 * Reshapes the legacy unpaged snapshot into the paged response shape, applying the filter, sort
 * and paging client-side.
 *
 * Stage numbers and plan positions are genuinely unavailable here, so they are left empty rather
 * than reconstructed from a hardcoded copy of the stage enums - keeping a client-side copy of
 * those is exactly the drift problem this work removes.
 */
const adaptLegacyHealthResponse = (
    legacy: SreLegacyHealthResponse,
    query: SreInflightSearchQuery
): SreInflightSearchResponse => {
    const stateNumberByName = new Map<string, number>(
        Object.entries(SRE_STAGE_STATE_NAMES).map(([value, name]) => [
            name,
            Number(value),
        ])
    );

    const items: SreInflightItem[] = (legacy.details ?? []).map(
        (detail, index) => ({
            id: detail.id ?? index,
            uniqueIdentifier: detail.uniqueIdentifier,
            stage: detail.stage,
            stageNumber: -1,
            stageOrder: null,
            stageCount: 0,
            stageState: detail.stageState,
            stageStateNumber:
                stateNumberByName.get(detail.stageState) ??
                SreStageState.InProgress,
            retryCount: detail.retryCount,
            writtenTime: detail.writtenTime,
            lastModifiedTime: detail.lastModifiedTime,
            orchestrationCount: detail.orchestrationCount,
            orchestrationType:
                'deploymentType' in detail ? detail.deploymentType : '',
            icmId: 0,
            latestCompletedStage: detail.latestCompletedStage,
            latestStatusMessage: detail.latestStatusMessage,
            stagedInLowshare: detail.stagedInLowshare,
            pipeline: 'pipeline' in detail ? detail.pipeline : undefined,
            region: 'region' in detail ? detail.region : undefined,
            artifactID: 'artifactID' in detail ? detail.artifactID : undefined,
            deploymentType:
                'deploymentType' in detail ? detail.deploymentType : undefined,
        })
    );

    const unsupported = [
        ...(query.stage?.length ? ['stage'] : []),
        ...(query.orchestrationType &&
        items.some((item) => !item.orchestrationType)
            ? ['orchestration type']
            : []),
        ...(query.stageState?.some((state) => ![0, 1, 3].includes(state))
            ? ['terminal state']
            : []),
    ];
    if (unsupported.length)
        throw new Error(
            `This SRE version cannot apply ${unsupported.join(
                ', '
            )} filters. Clear these filters or update SRE.`
        );
    const fleetMetrics = Object.entries(legacy.inflight ?? {}).flatMap(
        ([orchestrationType, metrics]) =>
            metrics.map((metric) => ({ ...metric, orchestrationType }))
    );
    const result = runLocalInflightQuery(items, query, [], fleetMetrics);
    result.degraded = true;
    return result;
};

/**
 * Searches in-flight orchestrations with filtering, sorting and paging.
 *
 * Falls back to the legacy unpaged endpoint on a 404, which is what happens when Hub is deployed
 * ahead of SRE in a given cloud. Only a 404 triggers the fallback; auth and server errors are
 * surfaced, because silently showing unfiltered data in a filtered-looking view is worse than an
 * error during an incident.
 */
export const searchSreInflight = async (
    type: SreEntityType,
    query: SreInflightSearchQuery,
    cancelToken?: CancelToken
): Promise<SreInflightSearchResponse> => {
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isSreHealthAvailable(cloud, environment)) {
        throw new Error(
            `Not a valid environment. Cloud: ${cloud} Environment: ${environment}`
        );
    }

    const agrmUrl = getSreHealthUrl();
    try {
        const response = await agpAxiosClient.get<
            RequestResult<SreInflightSearchResponse>
        >(
            `${agrmUrl}/api/${type}/health/search?${toSearchParams(
                query
            ).toString()}`,
            { cancelToken }
        );

        if (!response.data?.succeeded || !response.data.data) {
            throw new Error(
                response.data?.message ?? 'Search returned no data.'
            );
        }

        return response.data.data;
    } catch (error) {
        const status = (error as any)?.response?.status;

        if (status !== 404) {
            throw error;
        }

        if (
            cloud !== getConfig(GlobalConfigProperties.Cloud) ||
            environment !== getConfig(GlobalConfigProperties.Environment) ||
            agrmUrl !== getConfig(GlobalConfigProperties.AgrmUrl)
        ) {
            throw new Error(
                'SRE connection changed. Refresh the current cloud.'
            );
        }

        const legacy = await checkSreHealth(
            type === 'build' ? AgrmType.Build : AgrmType.Deployment,
            cancelToken
        );

        if (!legacy?.data) {
            throw error;
        }

        return adaptLegacyHealthResponse(legacy.data, query);
    }
};

/**
 * Every validation finding across the orchestrations matching a filter.
 *
 * Server side on purpose: the client only ever holds one page, so doing this from here would mean
 * one details request per matching orchestration - hundreds of calls for a fleet-wide filter.
 *
 * No 404 fallback. There is no legacy equivalent, so an older SRE deployment simply cannot answer
 * this and the caller should surface that rather than quietly return nothing.
 */
export const exportSreFindings = async (
    type: SreEntityType,
    query: SreInflightSearchQuery,
    cancelToken?: CancelToken,
    continuationToken?: string | null
): Promise<SreValidationFindingsResponse> => {
    if (type !== 'build')
        throw new Error('Only builds have validation findings.');

    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);

    if (!isSreHealthAvailable(cloud, environment)) {
        throw new Error(
            `Not a valid environment. Cloud: ${cloud} Environment: ${environment}`
        );
    }

    const agrmUrl = getSreHealthUrl();
    const parameters = toSearchParams(query);
    if (continuationToken)
        parameters.append('continuationToken', continuationToken);
    const response = await agpAxiosClient.get<
        RequestResult<SreValidationFindingsResponse>
    >(`${agrmUrl}/api/${type}/health/findings?${parameters.toString()}`, {
        cancelToken,
    });

    if (!response.data?.succeeded || !response.data.data) {
        throw new Error(response.data?.message ?? 'Findings export failed.');
    }

    return response.data.data;
};

//----------------------------------------------------------------------

// HELPERS---------------------------------------------------------------

//-----------------------------------------------------------------------

const getSreHealthUrl = (): string => {
    const url = getConfig(GlobalConfigProperties.AgrmUrl);
    if (!url?.trim()) {
        throw new Error(
            'No SRE (AGRM) URL is configured for this environment.'
        );
    }
    return url;
};

const createInvalidEnvironmentResult = <T>(
    cloud: string,
    environment: string
): RequestResult<T> => ({
    succeeded: false,
    message:
        'Not a valid environment. Cloud: ' +
        cloud +
        ' Environment: ' +
        environment,
    requestCharge: 0,
});

export const isValidCloudOrEnvironment = (
    cloud: string,
    environment: string
): boolean => {
    const isValid = isValidSreCloudOrEnvironment(cloud, environment);
    if (!isValid) {
        console.log(
            'Not a valid environment. Cloud: ' +
                cloud +
                ' Environment: ' +
                environment
        );
    }

    return isValid;
};
